import { chatWithCursorAgent, type CursorAgentProgressCallback } from './cursorAgentApi';
import { loadCursorAgentPreferences } from '../utils/cursorAgentPreferences';
import {
  applyRankingOrder,
  buildNewsRankingMessage,
  parseRankingReply,
} from '../utils/newsCursorRank';
import type { NewsFeedPreferences } from '../utils/newsFeedDefaults';
import type { NewsStory } from '../utils/newsStory';

const NEWS_RANK_AGENT_KEY = 'newsRank.agentId';

export function loadNewsRankAgentId(): string | null {
  try {
    return localStorage.getItem(NEWS_RANK_AGENT_KEY);
  } catch {
    return null;
  }
}

export function saveNewsRankAgentId(agentId: string | null): void {
  try {
    if (agentId) localStorage.setItem(NEWS_RANK_AGENT_KEY, agentId);
    else localStorage.removeItem(NEWS_RANK_AGENT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Send headlines to Cursor (same /api/cursor-agent proxy as AI chat).
 * Reuses a lightweight ranking agent id when available.
 */
export async function rankStoriesWithCursor(
  stories: readonly NewsStory[],
  prefs: Pick<NewsFeedPreferences, 'rankingPrompt' | 'maxRankedStories'>,
  onProgress?: CursorAgentProgressCallback,
  signal?: AbortSignal
): Promise<NewsStory[]> {
  if (signal?.aborted) return [];
  if (stories.length === 0) return [];

  const { apiKey } = loadCursorAgentPreferences();
  if (!apiKey.trim()) {
    throw new Error('Add your AI API key in Tools → Config to rank News.');
  }

  const maxPick = Math.min(prefs.maxRankedStories, stories.length);
  const message = buildNewsRankingMessage(stories, prefs.rankingPrompt, maxPick);

  const result = await chatWithCursorAgent(
    apiKey,
    message,
    loadNewsRankAgentId(),
    onProgress,
    { attachRepo: false }
  );

  if (signal?.aborted) return [];

  saveNewsRankAgentId(result.agentId);

  const order = parseRankingReply(result.reply, stories.length);
  if (order.length === 0) {
    throw new Error('AI returned no ranking — try Refresh again.');
  }

  return applyRankingOrder(stories, order).slice(0, maxPick);
}
